import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NotificationManager } from "react-notifications";
import { httpGet, httpPost } from "../helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import usePayStack from "../../hooks/usePayStack";
import "./archive.css";

const naira = (v) => `₦${Number(v).toLocaleString("en-NG")}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const longDay = (d) =>
  new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Lagos" });

export default function ArchiveCheckout({ editionId, page, onClose, history }) {
  const { userIsLogin } = useContext(UserDataContext);
  const { setPayStackConfig, PayWithPayStack } = usePayStack();
  const [state, setState] = useState("loading"); // loading | login | ready | paying | confirming | error
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const alive = useRef(true);

  const openReader = () => {
    onClose();
    history.push(`/view_single_paper/${editionId}${page ? `?page=${page}` : ""}`);
  };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && state !== "confirming" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, state]);

  // The server prices the edition and creates the payment reference.
  useEffect(() => {
    if (userIsLogin === false) {
      setState("login");
      return;
    }
    (async () => {
      // Ask the server straight away (it knows who is logged in) and never wait forever.
      const r = await Promise.race([
        httpPost("archive_checkout", { editionId }),
        new Promise((done) => setTimeout(() => done({ er: { message: "This is taking too long. Please check your connection and try again." } }), 20000)),
      ]);
      if (!alive.current) return;
      if (r && r.er && /unauthori|token|log ?in|expired/i.test(String(r.er.message || r.er))) {
        setState("login");
        return;
      }
      if (!r || r.er) {
        setError((r && r.er && (r.er.message || String(r.er))) || "Something went wrong. Please try again.");
        setState("error");
        return;
      }
      if (r.data && r.data.owned) {
        openReader();
        return;
      }
      setInfo(r.data);
      setPayStackConfig({ amount: r.data.amount, reference: r.data.reference, email: r.data.email });
      setState("ready");
    })();
  }, [editionId, userIsLogin]);

  // After Paystack says success, the server confirms with Paystack directly.
  const confirm = async () => {
    setState("confirming");
    for (let i = 0; i < 7; i += 1) {
      const r = await httpGet(`archive_checkout/verify/${info.reference}`);
      if (!alive.current) return;
      if (r && r.er) {
        setError(r.er.message || "The payment was not completed.");
        setState("error");
        return;
      }
      if (r && r.data && r.data.status === "paid") {
        NotificationManager.success("Payment confirmed. Enjoy your edition!");
        openReader();
        return;
      }
      await sleep(3000);
    }
    setError("We’re still confirming your payment with Paystack. It will appear in My library within a few minutes. You won’t be charged twice.");
    setState("error");
  };

  const pay = () => {
    setState("paying");
    PayWithPayStack(() => confirm(), () => setState("ready"));
  };

  let content;
  if (state === "loading") {
    content = <p className="ar-sub">Getting your edition ready…</p>;
  } else if (state === "login") {
    content = (
      <>
        <h2 id="ac-title" className="ar-title">Log in to buy this edition</h2>
        <div className="ar-actions">
          <button type="button" className="ar-btn ar-btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="ar-btn" onClick={() => { onClose(); history.push("/login"); }}>Log in</button>
        </div>
      </>
    );
  } else if (state === "confirming") {
    content = (
      <>
        <h2 id="ac-title" className="ar-title">Confirming your payment…</h2>
        <p className="ar-sub">This usually takes a few seconds. Please keep this window open.</p>
      </>
    );
  } else if (state === "error") {
    content = (
      <>
        <h2 id="ac-title" className="ar-title">We couldn’t finish this</h2>
        <p className="ar-sub">{error}</p>
        <div className="ar-actions">
          <button type="button" className="ar-btn ar-btn-outline" onClick={onClose}>Close</button>
          <button type="button" className="ar-btn" onClick={() => { onClose(); history.push("/account/library"); }}>My library</button>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <div className="ar-modal-head">
          <h2 id="ac-title" className="ar-title">Buy this edition</h2>
          <button type="button" className="ar-close" aria-label="Close" onClick={onClose} disabled={state === "paying"}>✕</button>
        </div>
        <div className="ac-summary">
          <span className="ac-cover">{info.edition.image ? <img src={info.edition.image} alt="" /> : null}</span>
          <span className="ac-details">
            <span className="ac-pub">{info.publication.name}</span>
            <span className="ac-date">{longDay(info.edition.publishedDate)}</span>
            <span className="ar-badge is-blue">Archive · {info.band}</span>
            <span className="ac-price">{naira(info.amount)}</span>
            {info.subscriberPrice ? <span className="ac-note">Subscriber price applied</span> : null}
          </span>
        </div>
        <div className="ar-promise"><strong>Yours to keep.</strong> It stays in My library and you can read it any time, on any device.</div>
        <button type="button" className="ar-btn" onClick={pay} disabled={state === "paying"}>
          {state === "paying" ? "Opening Paystack…" : `Pay ${naira(info.amount)} securely`}
        </button>
        <p className="ac-note" style={{ textAlign: "center" }}>Card, bank transfer or USSD, processed by Paystack.</p>
      </>
    );
  }

  return createPortal(
    <div className="ar-overlay" onMouseDown={(e) => e.target === e.currentTarget && state !== "confirming" && state !== "paying" && onClose()}>
      <div className="ar-modal" role="dialog" aria-modal="true" aria-labelledby="ac-title" style={{ maxWidth: 520 }}>
        {content}
      </div>
    </div>,
    document.body
  );
}
